import type { ItemSnapshot, Order, OrderSnapshot, Shipment } from "../types/DB2";

const PROCESSED_ITEM_STATUSES = new Set(["delivered", "damaged", "missing"]);

const coerceStatus = (value: unknown): string | undefined => {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed.length ? trimmed : undefined;
};

const normalizeDealerId = (value: unknown): string | undefined => {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed.length ? trimmed : undefined;
};

const normalizeDealerIdList = (value: unknown): string[] =>
  Array.isArray(value)
    ? (value as unknown[])
        .map((entry) => normalizeDealerId(entry))
        .filter((entry): entry is string => typeof entry === "string" && entry.length > 0)
    : [];

const cabinetRank = (value?: string | null) => {
  const s = (value ?? "").toString().trim();
  if (!s) return Number.POSITIVE_INFINITY;
  const matches = s.match(/\d+/g);
  if (!matches) return Number.POSITIVE_INFINITY;
  const nums = matches
    .map((seg) => parseInt(seg, 10))
    .filter((n) => Number.isFinite(n));
  return nums.length ? Math.min(...nums) : Number.POSITIVE_INFINITY;
};

export const compareByCabinet = (a: ItemSnapshot, b: ItemSnapshot) => {
  const ar = cabinetRank((a as any)?.cabinetNumber);
  const br = cabinetRank((b as any)?.cabinetNumber);
  if (ar !== br) return ar - br;
  const as = ((a as any)?.cabinetNumber ?? "").toString();
  const bs = ((b as any)?.cabinetNumber ?? "").toString();
  if (as !== bs) return as.localeCompare(bs);
  return (a.id || "").localeCompare(b.id || "");
};

export type OrderDisplayData = {
  orderId: string;
  snapshot: (OrderSnapshot & { preservedOrder?: Order | null }) | null;
  includedItems: ItemSnapshot[];
  excludedItems: ItemSnapshot[];
  destinations: string[];
  dealerLabel: string;
};

export const stripOrderPrefix = (
  orderId: string,
  snapshot?: (OrderSnapshot & { preservedOrder?: Order | null }) | null
): string => {
  const snap: any = snapshot || {};
  const prefix: string | undefined = snap?.prefix ?? snap?.preservedOrder?.prefix;
  const explicitOrderNo: string | undefined = snap?.orderNumber ?? snap?.preservedOrder?.orderNumber;

  const stripPrefixInsensitive = (value: string, p?: string): string => {
    const v = (value ?? "").toString().trim();
    if (!v) return "";
    const prefixTrim = (p ?? "").toString().trim();
    if (prefixTrim) {
      const variants = new Set<string>([prefixTrim]);
      if (!prefixTrim.endsWith("-")) variants.add(prefixTrim + "-");
      if (prefixTrim.endsWith("-")) variants.add(prefixTrim.slice(0, -1));
      for (const variant of variants) {
        if (!variant) continue;
        const len = variant.length;
        if (v.slice(0, len).toLowerCase() === variant.toLowerCase()) {
          return v.slice(len);
        }
      }
    }
    return v.replace(/^[A-Za-z]+-/, "");
  };

  if (explicitOrderNo && explicitOrderNo.toString().trim()) {
    return stripPrefixInsensitive(explicitOrderNo, prefix);
  }
  return stripPrefixInsensitive(orderId, prefix);
};

export const collectOrderDisplayData = (shipment: Shipment): OrderDisplayData[] => {
  if (!shipment.ordersSnapshot) return [];
  const entries = Object.entries(shipment.ordersSnapshot);

  return entries.map(([orderId, rawSnapshot]) => {
    const snapshot =
      (rawSnapshot as OrderSnapshot & {
        preservedOrder?: Order | null;
      }) ??
      ({
        orderId,
        includedItemsSnapshot: {},
        excludedItemsSnapshot: {},
      } as OrderSnapshot & { preservedOrder?: Order | null });

    const includedItems = Object.values(snapshot.includedItemsSnapshot ?? {}) as ItemSnapshot[];
    const excludedItems = Object.values(snapshot.excludedItemsSnapshot ?? {}) as ItemSnapshot[];

    includedItems.sort(compareByCabinet);
    excludedItems.sort(compareByCabinet);

    const preservedOrder = snapshot.preservedOrder ?? null;
    const explicitDestinations = Array.isArray(snapshot.destinations) ? snapshot.destinations : [];
    const preservedDestinations = Array.isArray(preservedOrder?.destinations) ? preservedOrder.destinations : [];

    const itemDestinations = includedItems
      .map((item) => (item.destination ? String(item.destination).trim() : ""))
      .filter((dest) => dest.length > 0);

    const destinations = Array.from(
      new Set(
        [...explicitDestinations, ...preservedDestinations, ...itemDestinations]
          .map((dest) => dest.trim())
          .filter((dest) => dest.length > 0)
      )
    );

  const normalizedShipmentDealerIds = normalizeDealerIdList((shipment as any)?.dealerIds);
  const shipmentDealerLabel = normalizedShipmentDealerIds.length
    ? normalizedShipmentDealerIds.join(", ")
    : undefined;
  const fallbackShipmentDealerId = normalizedShipmentDealerIds[0];
  const dealerLabel =
    normalizeDealerId(preservedOrder?.dealerId) ||
    normalizeDealerId(snapshot.dealerId) ||
    shipmentDealerLabel ||
    fallbackShipmentDealerId ||
      "Unknown dealer";

    return {
      orderId,
      snapshot,
      includedItems,
      excludedItems,
      destinations,
      dealerLabel,
    };
  });
};

export const getOrderStatus = (
  order: OrderDisplayData
): string | undefined => {
  const snap: any = order.snapshot || {};
  const direct =
    coerceStatus(snap.status) ??
    coerceStatus(snap.orderStatus) ??
    coerceStatus(snap?.preservedOrder?.status) ??
    coerceStatus(snap?.preservedOrder?.orderStatus);
  if (direct) {
    return direct;
  }

  const processedFlag =
    typeof snap.processed === "boolean"
      ? snap.processed
      : typeof snap?.preservedOrder?.processed === "boolean"
      ? snap.preservedOrder.processed
      : undefined;

  const hasIssuesFlag =
    typeof snap?.issueSummary?.hasIssues === "boolean"
      ? snap.issueSummary.hasIssues
      : typeof snap?.preservedOrder?.issueSummary?.hasIssues === "boolean"
      ? snap.preservedOrder.issueSummary.hasIssues
      : undefined;

  if (processedFlag === true) {
    if (hasIssuesFlag === false) {
      return "fulfilled";
    }
    if (hasIssuesFlag === true) {
      return "open";
    }
  }

  const itemsFromSnapshot: ItemSnapshot[] = snap.includedItemsSnapshot
    ? Object.values(snap.includedItemsSnapshot as Record<string, ItemSnapshot>)
    : [];
  const items = order.includedItems.length ? order.includedItems : itemsFromSnapshot;

  if (items.length > 0) {
    let processedCount = 0;
    let hasDamageOrMissing = false;
    items.forEach((item) => {
      const status = typeof item.status === "string" ? item.status.trim().toLowerCase() : "";
      if (PROCESSED_ITEM_STATUSES.has(status)) {
        processedCount += 1;
        if (status === "damaged" || status === "missing") {
          hasDamageOrMissing = true;
        }
      }
    });
    if (processedCount === items.length && !hasDamageOrMissing) {
      return "fulfilled";
    }
    if (processedCount > 0) {
      return "open";
    }
  }

  return undefined;
};
